// Device-wide "Pending uploads" badge + dropdown panel — lists EVERY photo still
// queued on THIS device across every record/module, not just whichever page is
// open. Lets the user jump to any record, retry a single group, or discard a
// single photo / a whole record's queue.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloudOff, RotateCw, Trash2, X } from "lucide-react";
import { getAllQueuedPhotos } from "@/lib/offlineDraft";
import { getUploadTarget } from "@/lib/photoUploadTargets";
import { flushDraftKey, discardOneQueuedPhoto, discardAllQueuedPhotos } from "@/lib/photoFlusher";

export function usePendingUploadsCount() {
    const [count, setCount] = useState(0);
    const refresh = useCallback(async () => {
        const all = await getAllQueuedPhotos();
        setCount(all.length);
    }, []);
    useEffect(() => {
        refresh();
        window.addEventListener("pr-photo-queue-changed", refresh);
        const interval = setInterval(refresh, 20000);
        return () => { window.removeEventListener("pr-photo-queue-changed", refresh); clearInterval(interval); };
    }, [refresh]);
    return count;
}

export function PendingUploadsPanel({ onClose }) {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const all = await getAllQueuedPhotos();
        const byKey = {};
        for (const q of all) (byKey[q.draftKey] ||= []).push(q);
        setGroups(Object.entries(byKey).map(([draftKey, items]) => {
            const entityType = items[0]?.meta?.entityType;
            const entityId = items[0]?.meta?.entityId;
            const target = getUploadTarget(entityType);
            return {
                draftKey, entityType, entityId, target,
                label: items[0]?.meta?.recordLabel || target?.label || entityType || "Unknown record",
                items: items.map((q) => ({ tempId: q.id, ...q.meta, previewUrl: URL.createObjectURL(q.blob) })),
            };
        }));
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
        window.addEventListener("pr-photo-queue-changed", load);
        return () => window.removeEventListener("pr-photo-queue-changed", load);
    }, [load]);

    const retryGroup = async (draftKey) => { await flushDraftKey(draftKey); await load(); };
    const discardItem = async (draftKey, tempId) => { await discardOneQueuedPhoto(draftKey, tempId); await load(); };
    const discardGroup = async (draftKey) => { await discardAllQueuedPhotos(draftKey); await load(); };
    const openRecord = (group) => {
        const path = group.target?.recordPath?.(group.entityId, group.items[0]);
        if (path) { navigate(path); onClose?.(); }
    };

    return (
        <>
            <div className="fixed inset-0 z-30" onClick={onClose} />
            <div data-testid="pending-uploads-panel"
                className="absolute right-0 top-[120%] bg-white border rounded-xl shadow-brand min-w-[360px] max-w-[400px] z-40 animate-fade-in overflow-hidden"
                style={{ borderColor: "#E5E0DA" }}>
                <div className="px-4 py-3 border-b" style={{ borderColor: "#F0EDE8" }}>
                    <span className="text-[13px] font-bold text-[#1F1F1F]">Pending photo uploads</span>
                    <p className="text-[11px] text-[#9A968F] mt-0.5">Saved on this device, waiting for signal</p>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                    {loading ? (
                        <div className="px-4 py-8 text-center text-[12px] text-[#9A968F]">Loading…</div>
                    ) : groups.length === 0 ? (
                        <div className="px-4 py-8 text-center text-[12px] text-[#9A968F]">
                            <CloudOff className="w-5 h-5 mx-auto mb-1.5 opacity-60" /> Nothing pending — all photos are uploaded.
                        </div>
                    ) : groups.map((g) => (
                        <div key={g.draftKey} data-testid={`pending-uploads-group-${g.draftKey}`} className="px-4 py-3 border-b" style={{ borderColor: "#F5F2EE" }}>
                            <div className="flex items-center justify-between gap-2">
                                <button type="button" onClick={() => openRecord(g)} data-testid={`pending-uploads-group-open-${g.draftKey}`}
                                    className="text-left text-[12px] font-semibold text-[#1F1F1F] hover:underline truncate">
                                    {g.label} <span className="text-[#9A968F] font-normal">· {g.items.length} photo{g.items.length !== 1 ? "s" : ""}</span>
                                </button>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button type="button" data-testid={`pending-uploads-group-retry-${g.draftKey}`} onClick={() => retryGroup(g.draftKey)}
                                        title="Retry now" className="w-6 h-6 grid place-items-center rounded hover:bg-[#F6F4F0] text-[#6F6A64]"><RotateCw size={13} /></button>
                                    <button type="button" data-testid={`pending-uploads-group-discard-${g.draftKey}`}
                                        onClick={() => { if (window.confirm(`Discard all ${g.items.length} pending photo(s) for ${g.label}?`)) discardGroup(g.draftKey); }}
                                        title="Discard all" className="w-6 h-6 grid place-items-center rounded hover:bg-[#FBEFE5] text-[#9F4212]"><Trash2 size={13} /></button>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {g.items.map((p) => (
                                    <div key={p.tempId} className="relative w-12 h-12">
                                        <img src={p.previewUrl} alt={p.filename} className={`w-full h-full object-cover rounded border-2 ${p.failed ? "border-[#B3261E]" : "border-dashed border-[#C9A66B]"}`} />
                                        <button type="button" data-testid={`pending-uploads-item-discard-${p.tempId}`}
                                            onClick={() => discardItem(g.draftKey, p.tempId)}
                                            className="absolute -top-1 -right-1 w-4 h-4 grid place-items-center rounded-full bg-black/55 text-white hover:bg-[#B33A2E]">
                                            <X size={9} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}
